/**
 * Action Center nav-badge count.
 *
 * Two layers in one place, since the hook is the pure tally's only consumer:
 *
 *  - `computeActionCenterCount` — a pure sum of the numbers shown on each card,
 *    across every pill the role can see (PM → PM + Scheduler + Moderator;
 *    Scheduler → Scheduler; Moderator → Moderator). Email Setup is a single
 *    setup task, so it contributes 1 while incomplete; every other card
 *    contributes its own item count (including session-assignment updates on
 *    the Moderator pill), and the shared No-primary-moderator count
 *    is added once (not again for the Scheduler pill).
 *  - `useActionCenterCount` — fetches only what the viewer's role needs (payment
 *    stats and flagged bonus requests for a PM, the schedule pool for anyone who
 *    can see a pill), reduces it
 *    to those per-card numbers, and feeds `computeActionCenterCount`.
 *    Recalculates on mount (page load) and whenever `recalcSignal` flips — the
 *    caller passes tab-entered/left state, matching the "recalc on navigation
 *    into/out of the tab, not real-time" requirement.
 */
import {useEffect, useState} from 'react';
import {useAuth} from '@/contexts/AuthContext';
import {api} from '@/shared/services/api-client';
import {
  useActionCenterDataFetchers,
  EMPTY_SCHEDULE_METRICS,
  type ScheduleMetrics,
} from './useActionCenterDataQuery';
import {filterVisibleNoShowLimitEntries, type BookingDecision} from '@/shared/lib/bookingDecisions';
import type {ModeratorActionSummary} from '@/shared/lib/schedule-moderators';
import {
  isPaymentApprovalsEnabled,
  isProjectEmailTemplatesEnabled,
} from '@/config/env';
import type {Project} from '@/shared/types/api';
import type {RoleFlags} from '../components/action-center-roles';
import {
  countConfiguredEmailTemplates,
  countPendingAvailabilityApprovals,
  filterPendingBonusRequests,
  shouldShowEmailSetupCard,
} from '../components/action-center-cards';

// --- Pure count ------------------------------------------------------------

/**
 * Derived per-card inputs (already-computed numbers plus feature-flag state),
 * kept flag-driven so this stays pure and independent of the API layer.
 */
export interface ActionCenterCountInput {
  flags: RoleFlags;
  paymentApprovalsEnabled: boolean;
  failedCount: number;
  emailTemplatesEnabled: boolean;
  configuredEmailTemplates: number;
  /** PM only: sessions flagged for bonus review and still awaiting a decision. */
  pendingBonusRequestCount: number;
  /** PM only: every payment_requests row still awaiting a decision, any source. */
  pendingPaymentRequestCount: number;
  /** PM only: distinct moderators with availability add/update/delete awaiting approval (OD-1259). */
  pendingAvailabilityApprovalCount: number;
  noPrimaryModeratorCount: number;
  /** OD-1102: (participant, session type) pairs at/over their no-show/cancellation limit. */
  noShowLimitCount: number;
  /** Moderator viewing their own pill. */
  upcomingCount: number;
  /** Moderator viewing their own pill. */
  cancelledCount: number;
  /** Moderator viewing their own pill. */
  noShowFollowUpCount: number;
  /** Moderator viewing their own pill: add/remove events on upcoming sessions. */
  mySessionChangeCount: number;
  /** PM viewing the Moderator pill: one entry per moderator with pending work. */
  moderatorSummaries: ModeratorActionSummary[];
  /**
   * OD-1343: the ownerless group's counts, for PM and Scheduler. Optional so a
   * caller that predates the group counts zero rather than failing to compile.
   */
  unassignedSummary?: {cancelledCount: number; noShowCount: number};
  /** Open failed/partial uploads this viewer may see. Counted once, not per tab. */
  openUploadActionCount: number;
}

/** The PM Moderator-pill rollup: every moderator's upcoming + cancelled + no-show items. */
/**
 * OD-1343: the ownerless group counts toward the badge like any other group
 * (AC-9). Kept separate from the named rollup so the Moderator role, which
 * never sees this group, never counts it either.
 */
const sumUnassignedItems = (input: ActionCenterCountInput): number =>
  (input.unassignedSummary?.cancelledCount ?? 0) +
  (input.unassignedSummary?.noShowCount ?? 0);

const sumRollupItems = (summaries: ModeratorActionSummary[]): number =>
  summaries.reduce(
    (total, summary) =>
      total + summary.upcomingCount + summary.cancelledCount + summary.noShowCount,
    0
  );

/**
 * PM view: Failed Payments (payment items) + Bonus Requests (one item per
 * pending flag, matching the number on the card) + Pending Payment Requests
 * (plain/unflagged requests only, so this never double-counts a row Bonus
 * Requests already shows) + Email Setup (1 while incomplete) + the shared
 * No-primary-moderator items (added once here, not again for the Scheduler
 * pill) + the no-show/cancellation-limit items (OD-1102, PM-only) + the
 * Moderator rollup items.
 *
 * Both payment cards are gated on the Payment Approvals flag: with it off
 * neither card renders, so neither may contribute to the badge.
 */
const sumPmItems = (input: ActionCenterCountInput): number => {
  const paymentItems = input.paymentApprovalsEnabled
    ? input.failedCount + input.pendingBonusRequestCount + input.pendingPaymentRequestCount
    : 0;
  const emailIncomplete =
    input.emailTemplatesEnabled &&
    shouldShowEmailSetupCard('pm', input.configuredEmailTemplates);
  return (
    paymentItems +
    (emailIncomplete ? 1 : 0) +
    input.pendingAvailabilityApprovalCount +
    input.noPrimaryModeratorCount +
    input.noShowLimitCount +
    input.openUploadActionCount +
    sumRollupItems(input.moderatorSummaries) +
    sumUnassignedItems(input)
  );
};

/** Moderator view: upcoming + cancelled + no-show-follow-up + session-assignment updates. */
const sumModeratorSelfItems = (input: ActionCenterCountInput): number =>
  input.upcomingCount +
  input.cancelledCount +
  input.noShowFollowUpCount +
  input.mySessionChangeCount +
  input.openUploadActionCount;

/**
 * Total open items for the badge, scoped to the viewer's role in the same order
 * the page resolves visibility (PM sees all pills; a Scheduler or Moderator sees
 * only their own). Returns 0 for a viewer with no recognized role.
 */
export const computeActionCenterCount = (
  input: ActionCenterCountInput
): number => {
  if (input.flags.isProjectManager) return sumPmItems(input);
  if (input.flags.isScheduler) {
    // OD-1343 AC-9: the ownerless group counts here, because nobody else is
    // counting it. The NAMED moderator groups deliberately still do not — a
    // scheduler's badge has always ignored them, and neither OD-1342 nor
    // OD-1343 asked to fold every moderator's workload into it.
    return (
      input.noPrimaryModeratorCount +
      input.openUploadActionCount +
      sumUnassignedItems(input)
    );
  }
  if (input.flags.isModerator) return sumModeratorSelfItems(input);
  return 0;
};

// --- Fetch + hook ----------------------------------------------------------

/** All fetched numbers backing the badge, before role scoping. */
interface BadgeMetrics extends ScheduleMetrics {
  failedCount: number;
  pendingBonusRequestCount: number;
  pendingPaymentRequestCount: number;
  pendingAvailabilityApprovalCount: number;
  mySessionChangeCount: number;
  openUploadActionCount: number;
}

const EMPTY_METRICS: BadgeMetrics = {
  failedCount: 0,
  pendingBonusRequestCount: 0,
  pendingPaymentRequestCount: 0,
  pendingAvailabilityApprovalCount: 0,
  mySessionChangeCount: 0,
  openUploadActionCount: 0,
  ...EMPTY_SCHEDULE_METRICS,
};

type ActionCenterDataFetchers = ReturnType<typeof useActionCenterDataFetchers>;

/**
 * Failed-payment count, or 0 when payments aren't in scope or the call fails.
 * Always requests a fresh value (`refresh: true`) — the badge must recalc on
 * every mount/tab-entry, not serve whatever the Action Center page (or a
 * prior mount) happened to cache moments ago. The shared fetcher still
 * dedupes this against a concurrent same-key request from the page itself.
 */
const fetchFailedCount = (
  fetchFailedPaymentCount: ActionCenterDataFetchers['fetchFailedPaymentCount'],
  projectId: string,
  enabled: boolean
): Promise<number> => {
  if (!enabled) return Promise.resolve(0);
  return fetchFailedPaymentCount(projectId, {refresh: true}).catch(() => 0);
};

/**
 * Pending bonus-request count, or 0 when the card isn't in scope or the call
 * fails. Fetched fresh for the same reason the failed count is.
 */
const fetchPendingBonusRequestCount = (
  fetchBonusFlagRequests: ActionCenterDataFetchers['fetchBonusFlagRequests'],
  projectId: string,
  enabled: boolean
): Promise<number> => {
  if (!enabled) return Promise.resolve(0);
  return fetchBonusFlagRequests(projectId, {refresh: true})
    .then((rows) => filterPendingBonusRequests(rows).length)
    .catch(() => 0);
};

/**
 * Pending plain (unflagged) payment-request count, or 0 when the card isn't
 * in scope or the call fails. Fetched fresh for the same reason the failed
 * count is.
 */
const fetchPendingPaymentRequestCount = (
  fetchPendingPaymentCount: ActionCenterDataFetchers['fetchPendingPaymentCount'],
  projectId: string,
  enabled: boolean
): Promise<number> => {
  if (!enabled) return Promise.resolve(0);
  return fetchPendingPaymentCount(projectId, {refresh: true}).catch(() => 0);
};

/** Distinct-moderator availability-approval count (OD-1259), PM-only. */
const fetchPendingAvailabilityApprovalCount = (
  fetchAvailabilityApprovals: ActionCenterDataFetchers['fetchAvailabilityApprovals'],
  projectId: string,
  enabled: boolean
): Promise<number> => {
  if (!enabled) return Promise.resolve(0);
  return fetchAvailabilityApprovals(projectId, {refresh: true})
    .then((rows) => countPendingAvailabilityApprovals(rows))
    .catch(() => 0);
};

/** Session-assignment updates on the Moderator self-card. Skipped for PM. */
const fetchMySessionChangeCount = (
  fetchMySessionChanges: ActionCenterDataFetchers['fetchMySessionChanges'],
  projectId: string,
  enabled: boolean
): Promise<number> => {
  if (!enabled) return Promise.resolve(0);
  return fetchMySessionChanges(projectId, {refresh: true})
    .then((rows) => (Array.isArray(rows) ? rows.length : 0))
    .catch(() => 0);
};

/** Schedule metrics for the project, or empties when the call fails. */
const fetchScheduleMetricsForBadge = (
  fetchScheduleMetrics: ActionCenterDataFetchers['fetchScheduleMetrics'],
  projectId: string,
  userId: string | null
): Promise<ScheduleMetrics> =>
  fetchScheduleMetrics(projectId, userId, {refresh: true}).catch(() => EMPTY_SCHEDULE_METRICS);

/**
 * OD-1102 follow-up: booking decisions, PM-only (the no-show/cancellation-
 * limit count is only ever added to the PM pill — see sumPmItems). Skipped
 * entirely for Scheduler/Moderator so their badge makes no extra request.
 */
/** Open upload items already scoped to this viewer by the server. */
const fetchOpenUploadActionCount = (projectId: string): Promise<number> =>
  api.uploadNotifications
    .actionItems(projectId)
    .then((res) => {
      const rows = (res as {data?: {data?: unknown}})?.data?.data;
      return Array.isArray(rows) ? rows.length : 0;
    })
    .catch(() => 0);

const fetchBookingDecisionsForBadge = (
  fetchBookingDecisions: ActionCenterDataFetchers['fetchBookingDecisions'],
  projectId: string,
  enabled: boolean
): Promise<BookingDecision[]> => {
  if (!enabled) return Promise.resolve([]);
  return fetchBookingDecisions(projectId, {refresh: true}).catch(() => []);
};

export interface UseActionCenterCountArgs {
  projectId: string;
  project: Project;
  flags: RoleFlags;
  /** Flips when the viewer enters/leaves the tab, forcing a recalc. */
  recalcSignal: boolean;
}

/** `{ count, loading }` for the Action Center nav badge. */
export const useActionCenterCount = ({
  projectId,
  project,
  flags,
  recalcSignal,
}: UseActionCenterCountArgs): {count: number; loading: boolean} => {
  const {user} = useAuth();
  const userId = user?.id ?? null;
  const {isProjectManager: isPM, isScheduler, isModerator} = flags;
  const {
    fetchFailedPaymentCount,
    fetchPendingPaymentCount,
    fetchBonusFlagRequests,
    fetchAvailabilityApprovals,
    fetchMySessionChanges,
    fetchScheduleMetrics,
    fetchBookingDecisions,
  } = useActionCenterDataFetchers();

  const [metrics, setMetrics] = useState<BadgeMetrics>(EMPTY_METRICS);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const hasRole = isPM || isScheduler || isModerator;
    if (!projectId || !hasRole) {
      setMetrics(EMPTY_METRICS);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    // Both payment cards are PM-only and behind the Payment Approvals flag.
    const paymentCardsInScope = isPM && isPaymentApprovalsEnabled();
    // Session-assignment updates are Moderator-self only — a PM on that pill
    // sees the per-moderator rollup instead, so they must not pick this up.
    const sessionChangesInScope = isModerator && !isPM;
    Promise.all([
      fetchFailedCount(fetchFailedPaymentCount, projectId, paymentCardsInScope),
      fetchPendingBonusRequestCount(
        fetchBonusFlagRequests,
        projectId,
        paymentCardsInScope
      ),
      fetchPendingPaymentRequestCount(
        fetchPendingPaymentCount,
        projectId,
        paymentCardsInScope
      ),
      fetchPendingAvailabilityApprovalCount(
        fetchAvailabilityApprovals,
        projectId,
        isPM
      ),
      fetchMySessionChangeCount(
        fetchMySessionChanges,
        projectId,
        sessionChangesInScope
      ),
      fetchScheduleMetricsForBadge(fetchScheduleMetrics, projectId, userId),
      fetchBookingDecisionsForBadge(fetchBookingDecisions, projectId, isPM),
      fetchOpenUploadActionCount(projectId),
    ]).then(
      ([
        failedCount,
        pendingBonusRequestCount,
        pendingPaymentRequestCount,
        pendingAvailabilityApprovalCount,
        mySessionChangeCount,
        schedule,
        decisions,
        openUploadActionCount,
      ]) => {
      if (cancelled) return;
      // "Allowed" is a permanent dismissal (no re-alert) — exclude it here too
      // so the badge can't drift from the Action Center card's own count.
      const noShowLimitCount = filterVisibleNoShowLimitEntries(
        schedule.noShowLimitEntries,
        decisions,
      ).length;
      setMetrics({
        failedCount,
        pendingBonusRequestCount,
        pendingPaymentRequestCount,
        pendingAvailabilityApprovalCount,
        mySessionChangeCount,
        openUploadActionCount,
        ...schedule,
        noShowLimitCount,
      });
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // fetchFailedPaymentCount/fetchScheduleMetrics/fetchBookingDecisions are
    // fresh closures every render (from useActionCenterDataFetchers) —
    // deliberately excluded so this only re-fetches when the inputs that
    // actually change the result change, matching the
    // useDemographicsTableFetcher/useTeamDataFetchers convention elsewhere in
    // this codebase.
  }, [projectId, isPM, isScheduler, isModerator, userId, recalcSignal]);

  const count = computeActionCenterCount({
    flags,
    paymentApprovalsEnabled: isPaymentApprovalsEnabled(),
    failedCount: metrics.failedCount,
    pendingBonusRequestCount: metrics.pendingBonusRequestCount,
    pendingPaymentRequestCount: metrics.pendingPaymentRequestCount,
    pendingAvailabilityApprovalCount: metrics.pendingAvailabilityApprovalCount,
    emailTemplatesEnabled: isProjectEmailTemplatesEnabled(),
    configuredEmailTemplates: countConfiguredEmailTemplates(
      project?.email_template_config
    ),
    noPrimaryModeratorCount: metrics.noPrimaryModeratorCount,
    noShowLimitCount: metrics.noShowLimitCount,
    upcomingCount: metrics.upcomingCount,
    cancelledCount: metrics.cancelledCount,
    noShowFollowUpCount: metrics.noShowFollowUpCount,
    mySessionChangeCount: metrics.mySessionChangeCount,
    moderatorSummaries: metrics.moderatorSummaries,
    unassignedSummary: metrics.unassignedSummary,
    openUploadActionCount: metrics.openUploadActionCount,
  });

  return {count, loading};
};
